import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useLazyListStoresQuery } from 'api/client-domain/client-domain';
import { useGetSMSTotalsByTypeQuery } from 'api/sms-reports-service/sms-reports-service';
import { SMSType } from 'api/sms-reports-service/sms-reports.type';
import { predefinedRangeOptions } from 'components/forms/fields/date-range-field/date-range-base-field/date-range-base-field.utils';
import {
  useLocationsToQueryString,
  useSearchQueryContext,
} from 'components/search/search.hooks';
import {
  defaultSearchParams,
  luxonDateSearchParam,
  stringListSearchParam,
} from 'components/search/search.utils';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

export const useSMSReportsSearchParams = () => {
  const { t } = useTranslation();

  const [triggerStores] = useLazyListStoresQuery();
  const locationsToQueryString = useLocationsToQueryString();

  return [
    ...defaultSearchParams({
      count: 10,
      page: 0,
    }),

    luxonDateSearchParam('startDate', {
      defaultValue: DateTime.local().startOf('week'),
      filter: {
        type: 'DateRangeFilter',
        label: t('forms.fields.dateRange.label'),
        startParamName: 'startDate',
        endParamName: 'endDate',
        predefinedDateOptions: predefinedRangeOptions([
          'this-quarter',
          'this-year',
          'last-quarter',
          'last-year',
        ]),
      },
    }),
    luxonDateSearchParam('endDate', {
      defaultValue: DateTime.local().endOf('day'),
    }),

    stringListSearchParam('storeIds', {
      defaultValue: ['ALL'],
      options: [
        {
          label: t(
            'page.transactions.filters.paymentAccountFilter.selectAllOption',
          ),
          id: 'ALL',
        },
      ],
      asyncOptions: async () => {
        const stores = await triggerStores({ count: 1000, page: 0 });
        const dataArray = stores?.data?.stores.map(store => ({
          label: store.title,
          id: store.storeId,
        }));

        return [
          {
            label: t('page.transactions.filters.statusFilter.all.label'),
            id: 'ALL',
          },
        ].concat(dataArray);
      },
      toQueryString: locationsToQueryString,
      filter: {
        type: 'MultiSelectFilter',
        label: t('page.salesreport.filters.stores'),
      },
    }),
  ];
};

/**
 * Fetches SMS totals for a single tab's summary header from
 * `/sms-totals-by-type`. Each tab passes its fixed SMS type
 * (`MARKETING` / `TRANSACTIONAL` / `ACCOUNT_VERIFICATION` /
 * `GUEST_CHECKOUT_VERIFICATION` / `PASSWORDLESS_AUTH`).
 */
export const useSMSTotalsSearch = (type: SMSType) => {
  const query = useSearchQueryContext();
  const { startDate, endDate } = query.queryState.originalArgs ?? {};

  const {
    data,
    isFetching,
    refetch: refetchSMSTotals,
  } = useGetSMSTotalsByTypeQuery(
    startDate && endDate
      ? {
          startDate,
          endDate,
          type,
        }
      : skipToken,
  );

  return {
    data,
    isFetching,
    refetchSMSTotals,
  };
};
